import type { ConsoleAlarm, ConsoleBusiness, ConsoleFeed, ConsoleQueueItem } from './api'

// SOC lead console (BRIEF-F1b): how a row is coloured from what the hub said.
// Nothing here decides anything about a business; it only colours the facts.

// A feed we are not reading. "stale" is a lagging poll, not a stop.
export const STOPPED_FEED_STATES = new Set(['dark', 'never_synced', 'sync_disabled'])
export const SPEND_AMBER_RATIO = 0.8
export const QUEUE_RED_AFTER_HOURS = 24

export const FEED_STATE_LABEL: Record<string, string> = {
  fresh: 'reading',
  stale: 'behind',
  dark: 'stopped',
  never_synced: 'never read',
  sync_disabled: 'switched off',
}

export function feedStopped(feed: ConsoleFeed): boolean {
  return STOPPED_FEED_STATES.has(feed.state)
}

// A watched person whose feed stopped. An unticked person's feed is shown but
// does not turn the row red: nobody is reading that person by choice.
export function stoppedFeeds(b: ConsoleBusiness): ConsoleFeed[] {
  return b.feeds.filter((f) => f.watched && feedStopped(f))
}

// The readout day had people not read or not seen. Only while the shift is
// on: a business with its shift off is not read by design, and says so. A
// shift that is on with nobody to read has read nobody, which is not a good day.
export function dayNotSeen(b: ConsoleBusiness): boolean {
  return b.watched && (b.readout_headline.not_read_or_not_seen > 0 || b.readout_headline.people === 0)
}

export function spendRatio(b: ConsoleBusiness): number {
  if (!(b.daily_cap_usd > 0)) return 0
  return b.spent_today_usd / b.daily_cap_usd
}

export type RowTone = 'red' | 'amber' | 'plain'

export function businessTone(b: ConsoleBusiness): RowTone {
  if (stoppedFeeds(b).length > 0 || dayNotSeen(b)) return 'red'
  if (spendRatio(b) >= SPEND_AMBER_RATIO) return 'amber'
  return 'plain'
}

export function ageHours(iso: string | null, now: Date): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return Math.max(0, (now.getTime() - t) / 3_600_000)
}

export function ageLabel(hours: number | null): string {
  if (hours === null) return 'age unknown'
  if (hours < 1) return 'under an hour'
  if (hours < 48) return `${Math.floor(hours)} h`
  return `${Math.floor(hours / 24)} days`
}

export function queueItemOverdue(item: ConsoleQueueItem, now: Date): boolean {
  const h = ageHours(item.opened_at, now)
  return h !== null && h > QUEUE_RED_AFTER_HOURS
}

export function unacknowledgedCount(alarms: ConsoleAlarm[]): number {
  return alarms.filter((a) => !a.acknowledged_at).length
}

// "Could not load: <reason>": the route's own detail when it gave one,
// otherwise what the browser saw.
export function loadReason(err: unknown): string {
  const e = err as {
    response?: { status?: number; data?: { detail?: unknown } }
    message?: unknown
  }
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (e?.response?.status) return `the server answered ${e.response.status}`
  if (typeof e?.message === 'string' && e.message) return e.message
  return 'no answer from the server'
}

export function money(usd: number): string {
  return `$${usd.toFixed(2)}`
}
